import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CircleNotch, Play, CheckCircle, WarningCircle, Trash, WindowsLogo } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;
const T = '#0078D4';

const TIER_DESC = {
  z01: 'Low-quota subscription (~3 Front Door profiles).',
  z02: 'Higher-quota subscription.',
  z03: 'High-quota subscription (effectively unlimited for our use).',
};

const AzureTester = () => {
  const [form, setForm] = useState({ label: '', tenant_id: '', client_id: '', client_secret: '', subscription_id: '' });
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = async () => { try { const r = await axios.get(`${API}/api/azure-tester/history`, { withCredentials: true }); setHistory(r.data); } catch {} };
  useEffect(() => { loadHistory(); }, []);

  const run = async () => {
    if (!form.tenant_id || !form.client_id || !form.client_secret || !form.subscription_id) { toast.error('All four credentials are required'); return; }
    setRunning(true); setResult(null);
    try {
      const r = await axios.post(`${API}/api/azure-tester/run`, form, { withCredentials: true });
      setResult(r.data);
      toast.success(`Detected ${r.data.tier || 'unknown'} · limit ${r.data.max_urls}`);
      loadHistory();
    } catch (e) { toast.error(e.response?.data?.detail || 'Test failed'); loadHistory(); }
    finally { setRunning(false); }
  };

  const del = async (id) => { try { await axios.delete(`${API}/api/azure-tester/history/${id}`, { withCredentials: true }); loadHistory(); } catch {} };

  const F = (k, ph, type = 'text') => (
    <input value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} placeholder={ph} type={type}
      data-testid={`azt-${k}`} className="bg-[#111318] border border-white/10 p-2.5 text-white text-sm focus:outline-none focus:border-[#0078D4] w-full" />
  );

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div><h1 className="text-4xl font-black mb-2 flex items-center gap-3" style={{ color: T }}><WindowsLogo size={38} weight="fill" /> AZURE ACCOUNT TESTER</h1>
        <p className="text-slate-400 font-mono">Provisions a throwaway Front Door endpoint, detects the account ring (z01/z02/z03) + creation limit, then deletes it.</p></div>

      <div className="bg-card border border-white/10 p-6 space-y-4 max-w-3xl">
        <div className="grid md:grid-cols-2 gap-3">
          {F('label', 'Label (optional, e.g. Client A sub)')}
          {F('subscription_id', 'Subscription ID')}
          {F('tenant_id', 'Tenant ID')}
          {F('client_id', 'Client ID (App ID)')}
        </div>
        {F('client_secret', 'Client Secret', 'password')}
        <button onClick={run} disabled={running} data-testid="azt-run-btn" className="flex items-center gap-2 text-white px-6 py-3 font-bold text-sm uppercase tracking-wider disabled:opacity-50" style={{ background: T }}>
          {running ? <CircleNotch size={18} className="animate-spin" /> : <Play size={18} weight="fill" />} {running ? 'Provisioning test FD…' : 'Run Test'}
        </button>
        <p className="text-[11px] text-slate-500">Creates 1 temporary Front Door endpoint to read the ring from its <span className="font-mono">*.azurefd.net</span> URL, then removes it. Takes ~30–90s.</p>
      </div>

      {result && (
        <div className="bg-card border p-6 max-w-3xl" style={{ borderColor: T + '66' }} data-testid="azt-result">
          <div className="flex items-center gap-3 mb-4"><CheckCircle size={24} className="text-[#00E676]" />
            <div className="text-2xl font-black" style={{ color: T }}>Account type: {result.tier || 'unknown'}</div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <div className="bg-[#0A0B0E] border border-white/10 p-3"><div className="text-xs text-slate-500 uppercase">Creation limit</div><div className="text-white font-bold text-lg">{result.max_urls} URLs</div></div>
            <div className="bg-[#0A0B0E] border border-white/10 p-3"><div className="text-xs text-slate-500 uppercase">Test FD response</div><div className="text-white font-bold text-lg">HTTP {result.http_status ?? '—'}</div></div>
          </div>
          <div className="mt-3 text-xs text-slate-400">{TIER_DESC[result.tier] || 'Ring not detected from the endpoint hostname.'}</div>
          {result.test_url && <div className="mt-2 font-mono text-xs text-slate-500 break-all">Test URL: {result.test_url}</div>}
        </div>
      )}

      <div>
        <h2 className="text-lg font-bold text-slate-300 mb-3">Past checks</h2>
        <div className="space-y-2">
          {history.map(h => (
            <div key={h.id} className="bg-card border border-white/10 p-3 flex items-center justify-between gap-3" data-testid={`azt-hist-${h.id}`}>
              <div className="flex items-center gap-3">
                {h.ok ? <CheckCircle size={18} className="text-[#00E676]" /> : <WarningCircle size={18} className="text-red-400" />}
                <div>
                  <div className="text-white text-sm font-bold">{h.label} · <span style={{ color: T }}>{h.tier || 'failed'}</span>{h.ok && ` · limit ${h.max_urls}`}</div>
                  <div className="text-[11px] text-slate-500 font-mono">tenant …{h.tenant_last4} · sub …{h.sub_last4} · {new Date(h.created_at).toLocaleString()}{h.error ? ` · ${h.error.slice(0,60)}` : ''}</div>
                </div>
              </div>
              <button onClick={() => del(h.id)} data-testid={`azt-del-${h.id}`} className="text-slate-500 hover:text-red-400"><Trash size={15} /></button>
            </div>
          ))}
          {history.length === 0 && <div className="text-slate-500 text-center py-8">No checks yet.</div>}
        </div>
      </div>
    </div>
  );
};
export default AzureTester;
